import { Loader2, Sparkles, Trash2, Upload, X } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

const ACCEPTED = ".pdf,.png,.jpg,.jpeg,.webp,.heic";

/** Panel donde el profesional adjunta PDFs/imágenes de referencia para que la IA
 *  las considere al generar la receta. Se almacenan con kind="pro_reference". */
export function ProReferencesPanel({ req, onChange, completed }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(null);

  const refs = (req.files || []).filter((f) => f.kind === "pro_reference");
  const aiActive = req.ai_status === "generating";

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/requests/${req.id}/files/pro`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Referencia adjuntada");
      await onChange?.();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
    }
  };

  const onRemove = async (fileId) => {
    setRemoving(fileId);
    try {
      await api.delete(`/requests/${req.id}/files/pro/${fileId}`);
      toast.success("Referencia eliminada");
      await onChange?.();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setRemoving(null);
    }
  };

  if (completed) return null;

  return (
    <div className="border rounded-2xl bg-white p-5" data-testid="pro-references-panel">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="h-4 w-4 text-primary" strokeWidth={1.5} />
        <p className="font-heading font-semibold">Material extra para la IA</p>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Adjunta PDFs o imágenes que la IA debe considerar al redactar la receta
        (guías clínicas, estudios extra, notas privadas, referencias). Máx. 10 MB cada uno.
        El paciente no las recibe.
      </p>

      {refs.length > 0 && (
        <ul className="space-y-2 mb-3" data-testid="pro-references-list">
          {refs.map((f) => (
            <li
              key={f.file_id}
              className="flex items-center justify-between gap-2 border rounded-xl px-3 py-2 text-sm bg-secondary/30"
              data-testid={`pro-reference-${f.file_id}`}
            >
              <span className="truncate flex-1" title={f.original_filename}>
                {f.original_filename}
              </span>
              <button
                type="button"
                onClick={() => onRemove(f.file_id)}
                disabled={removing === f.file_id || aiActive}
                className="text-red-600 hover:text-red-800 disabled:opacity-50"
                aria-label="Eliminar referencia"
                data-testid={`pro-reference-remove-${f.file_id}`}
              >
                {removing === f.file_id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <X className="h-4 w-4" strokeWidth={1.5} />
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      <input
        ref={fileRef}
        type="file"
        accept={ACCEPTED}
        className="hidden"
        onChange={onPick}
        data-testid="pro-reference-file-input"
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="rounded-full"
        onClick={() => fileRef.current?.click()}
        disabled={uploading || aiActive}
        data-testid="pro-reference-upload-btn"
      >
        {uploading ? (
          <>
            <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Subiendo...
          </>
        ) : (
          <>
            <Upload className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Adjuntar referencia
          </>
        )}
      </Button>
      {aiActive && refs.length > 0 && (
        <p className="text-xs text-muted-foreground mt-2 italic" data-testid="pro-reference-locked-hint">
          Las referencias se aplicarán a la siguiente regeneración.
        </p>
      )}
    </div>
  );
}
